import { getSupabaseClient } from './supabase-client';
import { readSupabaseEnv } from '../lib/env';
import { reportError } from '../lib/monitoring';

/**
 * Asking Crewlink to organize a Capture. M7 / S07-02.
 *
 * **THIS IS ONE AUTHENTICATED CALL TO CREWLINK'S OWN ENDPOINT, AND NOTHING
 * ELSE.** No provider is named here, no model is chosen here, no prompt exists
 * here, and no key is reachable from here — all of that is a server-side fact
 * the client is not told and does not need (D-012). The vendor is deliberately
 * not named even in a comment, so `check:secrets:build` can assert the strong
 * property: the name appears in no client source and in no built artifact.
 *
 * **THE CLIENT NEVER CALLS `begin_capture_organization` ITSELF.** Admission —
 * whether a model call may be spent at all — is decided inside the endpoint,
 * in the same transaction that holds the Capture lock. If the app called
 * `begin` and then the endpoint separately, anyone could call the endpoint
 * directly and skip the attempt ceiling. One door, one decision.
 *
 * **THE TRANSCRIPT IS NEVER SENT.** The request carries a Capture id. The
 * server reads the accepted transcript itself, through the caller's own
 * token, so RLS decides what may be read. A client that could supply the text
 * could supply anything.
 *
 * **FAILURE HERE IS ALWAYS HARMLESS.** Organization is derived data. Every
 * caller below fires this and moves on: a Capture saves, a transcript edit
 * lands, and neither depends on this returning at all (INV-T27, PT §10).
 */

export const ORGANIZE_FUNCTION_NAME = 'organize-capture';

/**
 * What the endpoint says happened. Deliberately small — the screen needs the
 * organization ROW (which it re-reads), not a narrative from here.
 */
export type OrganizeStatus =
  'organized' | 'needs_review' | 'not_admitted' | 'stale' | 'failed' | 'unavailable';

export interface OrganizeResult {
  readonly status: OrganizeStatus;
  /** Present for `not_admitted`: why the database declined to spend a call. */
  readonly reason?: string;
}

/**
 * In-flight Capture ids — **an optimization, never a correctness boundary.**
 *
 * It stops a screen that mounts twice from making two HTTP calls. It cannot
 * stop anything else: it lives in one JavaScript process and disappears when
 * that process dies. The real duplicate-work guard is
 * `begin_capture_organization`, which holds the Capture row lock and refuses a
 * second attempt for a version already being organized. Tests assert that,
 * not this.
 */
const inFlight = new Set<string>();

export async function requestCaptureOrganization(
  captureId: string,
  options: { readonly explicitRetry?: boolean } = {},
): Promise<OrganizeResult> {
  if (inFlight.has(captureId)) return { status: 'not_admitted', reason: 'request_in_flight' };

  const env = readSupabaseEnv();
  const client = getSupabaseClient();
  if (!env || !client) return { status: 'unavailable' };

  inFlight.add(captureId);
  try {
    const { data, error } = await client.auth.getSession();
    if (error) return { status: 'unavailable' };
    const accessToken = data.session?.access_token;
    if (!accessToken) return { status: 'unavailable' };

    const response = await fetch(
      `${env.url.replace(/\/+$/, '')}/functions/v1/${ORGANIZE_FUNCTION_NAME}`,
      {
        method: 'POST',
        headers: {
          apikey: env.anonKey,
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        // The id, and whether this was a deliberate retry. Nothing else — and
        // the endpoint REFUSES any other field rather than ignoring it.
        body: JSON.stringify(
          options.explicitRetry === true ? { captureId, explicitRetry: true } : { captureId },
        ),
      },
    );

    if (!response.ok) return { status: 'unavailable' };

    const payload = (await response.json()) as { status?: unknown; reason?: unknown };
    const status = payload.status;
    if (
      status === 'organized' ||
      status === 'needs_review' ||
      status === 'stale' ||
      status === 'failed'
    ) {
      return { status };
    }
    if (status === 'not_admitted') {
      return {
        status,
        reason: typeof payload.reason === 'string' ? payload.reason : 'unknown',
      };
    }
    return { status: 'unavailable' };
  } catch (caught) {
    reportError(caught, 'capture.organize');
    return { status: 'unavailable' };
  } finally {
    inFlight.delete(captureId);
  }
}

/**
 * WHO WANTS TO KNOW WHEN A BACKGROUND ATTEMPT HAS FINISHED — M7 post-closeout.
 *
 * **THE DEFECT THIS EXISTS TO FIX.** `requestCaptureOrganizationInBackground`
 * was fired and forgotten, and nothing was listening. The database ended up
 * perfectly correct — state `organized`, a real location, a real caption — and
 * **every screen already on the phone kept showing the snapshot it had read
 * seconds earlier**. The only way to see the result was an unrelated mutation
 * that happened to reload, or leaving the screen and coming back. Brandon
 * reasonably read "press Edit, then Save" as the way to make a location appear,
 * which turns a derived value into something he has to accept by hand.
 *
 * **THIS IS A READ SIGNAL AND NOTHING ELSE.** Subscribers re-read server truth.
 * No subscriber may organize, write, or ask for anything — a listener that
 * requested organization would loop, and would spend money doing it.
 *
 * Deliberately NOT: polling · Realtime · a timer · a global store. One Set of
 * callbacks beside the `inFlight` Set that already lives in this module, fired
 * exactly once per background attempt.
 */
type OrganizationSettledListener = (captureId: string) => void;

const settledListeners = new Set<OrganizationSettledListener>();

/** Subscribe. Returns the unsubscribe, which callers MUST run on unmount. */
export function onCaptureOrganizationSettled(listener: OrganizationSettledListener): () => void {
  settledListeners.add(listener);
  return () => {
    settledListeners.delete(listener);
  };
}

/**
 * ONE LISTENER MUST NEVER BE ABLE TO SILENCE ANOTHER, so each is called inside
 * its own try. A screen that throws while refreshing is its own problem; it is
 * not allowed to stop a different screen from catching up.
 *
 * The Set is copied first: a listener that unsubscribes during the loop —
 * which is exactly what an unmounting screen does — must not perturb the
 * iteration of the others.
 */
function notifyOrganizationSettled(captureId: string): void {
  for (const listener of [...settledListeners]) {
    try {
      listener(captureId);
    } catch (caught) {
      reportError(caught, 'capture.organizeSettled');
    }
  }
}

/**
 * Ask for organization and never let the answer matter.
 *
 * Used by the two places that trigger it — a Capture that has just saved, and
 * a transcript that has just been corrected. **Both have already succeeded by
 * the time this runs**, and neither may be undone, delayed or reported as
 * failed because a derived value could not be computed (PT §10).
 *
 * **IT STILL DOES NOT MATTER, AND IT IS NO LONGER INVISIBLE.** When the attempt
 * settles — organized, needs_review, failed, refused admission or unreachable —
 * subscribers are told, and they re-read. The OUTCOME is deliberately not
 * passed on: the row is the truth, the reply is a narration of it, and a screen
 * that trusted the narration could show something the database never stored.
 */
export function requestCaptureOrganizationInBackground(captureId: string): void {
  // **THIS FUNCTION MUST NOT THROW, SYNCHRONOUSLY OR OTHERWISE.** Both callers
  // invoke it inside the `try` that guards a save or a transcript edit, so a
  // synchronous throw here would be caught by THEIR error handler and would
  // turn a Capture that saved perfectly into one reported as failed. The
  // rejection handler alone is not enough: anything that throws before the
  // promise is returned would escape it.
  try {
    void requestCaptureOrganization(captureId)
      .catch((caught: unknown) => {
        reportError(caught, 'capture.organize');
      })
      // EVERY OUTCOME NOTIFIES, INCLUDING THE FAILURES. A refused admission
      // still means the row may have moved; a failure is what makes Retry
      // appear. Notifying only on success would leave exactly the states
      // Brandon most needs to see stuck on the previous snapshot.
      .finally(() => {
        notifyOrganizationSettled(captureId);
      });
  } catch (caught) {
    reportError(caught, 'capture.organize');
    // The request never started, so nothing changed and there is nothing to
    // re-read. Notifying here would ask every mounted screen to fetch for no
    // reason.
  }
}
